// mentionSelectionPlugin.js
// Plugin to add "ProseMirror-selectednode" class to mention nodes when they are in a TextSelection
import {Plugin, PluginKey} from "prosemirror-state";
import {Decoration, DecorationSet} from "prosemirror-view";

export const mentionSelectionPluginKey = new PluginKey("mentionSelection");

/**
 * Create a plugin that adds "ProseMirror-selectednode" class to mention nodes
 * within a TextSelection (mouse drag or Shift+Arrows)
 *
 * ProseMirror automatically adds this class for NodeSelection (clicking on atom),
 * but not for TextSelection that includes atom nodes.
 *
 * @returns {Plugin}
 */
export function mentionSelectionPlugin() {
    return new Plugin({
        key: mentionSelectionPluginKey,

        state: {
            init(_, state) {
                return createSelectionDecorations(state);
            },

            apply(tr, decorationSet, oldState, newState) {
                // Only update if selection changed or document changed
                const selectionChanged = !oldState.selection.eq(newState.selection);
                const docChanged = tr.docChanged;

                if (selectionChanged || docChanged) {
                    return createSelectionDecorations(newState);
                }

                // Map decorations through the transaction
                return decorationSet.map(tr.mapping, tr.doc);
            }
        },

        props: {
            decorations(state) {
                return this.getState(state);
            }
        }
    });
}

/**
 * Create decorations for mention nodes within the current selection
 * @param {EditorState} state - ProseMirror editor state
 * @returns {DecorationSet}
 */
function createSelectionDecorations(state) {
    const {doc, selection} = state;
    const decorations = [];

    // Only process TextSelection (not NodeSelection, which already gets the class)
    if (selection.empty || selection.constructor.name === "NodeSelection") {
        return DecorationSet.empty;
    }

    const {from, to} = selection;

    // Find all mention nodes within the selection
    doc.nodesBetween(from, to, (node, pos) => {
        // Check if this is a mention node (userMention or hashtag)
        if (node.type.name === "userMention" || node.type.name === "hashtag") {
            // Add decoration to apply ProseMirror-selectednode class
            decorations.push(
                Decoration.node(pos, pos + node.nodeSize, {
                    class: "ProseMirror-selectednode"
                })
            );
        }
    });

    return DecorationSet.create(doc, decorations);
}

